import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, FileSignature, ShieldCheck, ArrowUpDown } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Feature toggle for Assignment ID visibility
const SHOW_ASSIGNMENT_ID = import.meta.env.VITE_SHOW_ASSIGNMENT_ID !== 'false';

// Live API Assignment Interface
export interface LiveAssignment {
  id: number;
  campaignTaskId?: number;
  campaignId: number;
  campaignName?: string;
  storeId: number;
  storeName?: string;
  storeAddress?: string;
  operatorId: number;
  operatorName?: string;
  operatorUsername?: string;
  cycleName?: string;
  taskId: number;
  taskName?: string;
  status: 'pending' | 'operator_complete' | 'admin_complete' | 'admin_pending_review' | 'ready_for_operator';
  archived?: boolean;
  createdAt: string;
  updatedAt: string;
  adminApprovedAt?: string | null;
  adminApprovedBy?: number | null;
  userId?: number;
  postedUnits?: number;
  hasEvidence?: boolean;
  mediaRequirements: Array<{
    formatId: number;
    formatName: string;
    formatType: string;
    quantity: number;
    expected?: number;
    delivered?: number;
    discrepancy?: string;
    status?: string;
    eventId?: number;
  }>;
  postingEvents: Array<{
    eventId: number;
    postedUnits: number;
    notes?: string;
    installerNote?: string;
    timestamp: string;
    groupId?: number | null;
    groupName?: string | null;
  }>;
  packType?: 'standard' | 'flexible';
  packName?: string;
  packId?: number;
  selectedGroup?: {
    id: number;
    name: string;
  } | null;
  packEvidence?: {
    signOffType?: 'admin' | 'operator';
    signedOffAt?: string;
  };
  // Phase 5: Task type (POSTING or REMOVAL)
  taskType?: 'POSTING' | 'REMOVAL';
}

export interface ProofOfPostingColumnHandlers {
  onAdminSignOff: (assignment: LiveAssignment) => void;
  onViewDetails: (storeId: number, operatorId: number, assignmentId: number) => void;
  getCycleName: () => string;
  validateOperatorSignature: (assignment: LiveAssignment) => boolean;
}

export function createProofOfPostingColumns(
  handlers: ProofOfPostingColumnHandlers
): ColumnDef<LiveAssignment>[] {
  const { onAdminSignOff, onViewDetails, getCycleName, validateOperatorSignature } = handlers;

  const columns: ColumnDef<LiveAssignment>[] = [
    // Task Type column (POSTING or REMOVAL)
    {
      id: 'taskType',
      header: 'Type',
      cell: ({ row }) => {
        const isRemoval = row.original.taskType === 'REMOVAL';
        return (
          <Badge 
            variant="outline" 
            className={isRemoval 
              ? "text-xs bg-orange-100 text-orange-800 border-orange-300 font-medium" 
              : "text-xs bg-blue-100 text-blue-800 border-blue-300 font-medium"
            }
          >
            {isRemoval ? 'Removal' : 'Posting'}
          </Badge>
        );
      },
    },
    // Cycle column
    {
      id: 'cycle',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Cycle
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: () => (
        <Badge variant="outline" className="font-mono text-xs">
          {getCycleName()}
        </Badge>
      ),
    },
    // Campaign column
    {
      accessorKey: 'campaignName',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Campaign
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const assignment = row.original;
        const isPending = assignment.status === 'pending';
        const isRemoval = assignment.taskType === 'REMOVAL';

        return (
          <div>
            <div className="flex items-center gap-2">
              <span className={`font-medium ${isPending ? 'text-gray-500' : ''}`}>
                {assignment.campaignName}
              </span>
              {isRemoval && (
                <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                  Removal
                </Badge>
              )}
            </div>
            <div className="text-sm text-gray-500">{assignment.taskName}</div>
            {/* Flexible pack group selection display */}
            {assignment.packType === 'flexible' && (
              <div className="mt-1">
                {assignment.selectedGroup ? (
                  <Badge
                    variant="outline"
                    className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                  >
                    Selected Group: {assignment.selectedGroup.name}
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="text-xs bg-orange-50 text-orange-700 border-orange-200"
                  >
                    ⚠️ No group selected by operator
                  </Badge>
                )}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  // Conditionally add Assignment ID column
  if (SHOW_ASSIGNMENT_ID) {
    columns.push({
      accessorKey: 'id',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Assignment ID
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <Badge variant="outline" className="font-mono text-xs">
          {row.original.id}
        </Badge>
      ),
    });
  }

  // Store column
  columns.push({
    accessorKey: 'storeName',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Store
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const assignment = row.original;
      const isPending = assignment.status === 'pending';

      return (
        <div>
          <div className={`font-medium ${isPending ? 'text-gray-500' : ''}`}>
            {assignment.storeName}
          </div>
          <div className="text-sm text-gray-500">{assignment.storeAddress}</div>
        </div>
      );
    },
  });

  // Operator column
  columns.push({
    accessorKey: 'operatorName',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Operator
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const assignment = row.original;
      const isPending = assignment.status === 'pending';

      return (
        <div>
          <div className={`font-medium ${isPending ? 'text-gray-500' : ''}`}>
            {assignment.operatorName}
          </div>
          <div className="text-sm text-gray-500">@{assignment.operatorUsername}</div>
        </div>
      );
    },
  });

  // Evidence column
  columns.push({
    id: 'evidence',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Evidence
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const assignment = row.original;
      const status = assignment.status;
      const isPending = status === 'pending';
      const totalFormats = assignment.mediaRequirements?.length || 0;

      return (
        <div className="flex items-center gap-2">
          {isPending ? (
            <Badge variant="secondary" className="text-gray-600">
              Pack not submitted
            </Badge>
          ) : status === 'operator_complete' ? (
            <Badge variant="outline" className="text-green-700 border-green-300">
              Pack submitted ({totalFormats} formats)
            </Badge>
          ) : status === 'admin_complete' ? (
            <Badge variant="success">
              Pack approved ({totalFormats} formats)
            </Badge>
          ) : (
            <Badge variant="outline" className="text-blue-600 border-blue-300">
              Pack in progress
            </Badge>
          )}
          {isPending && (
            <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
              Awaiting Operator Submission
            </Badge>
          )}
        </div>
      );
    },
  });

  // Status column
  columns.push({
    accessorKey: 'status',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2"
        >
          Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const assignment = row.original;
      const status = assignment.status;
      const isPending = status === 'pending';

      if (status === 'admin_complete') {
        return (
          <div className="flex items-center gap-2">
            {assignment.packEvidence?.signOffType === 'admin' ? (
              <Badge variant="info">
                🛂 Admin Sign-Off
              </Badge>
            ) : (
              <Badge variant="success">
                🛡️ Signed by Admin
              </Badge>
            )}
            <span className="text-xs text-gray-500">
              {assignment.packEvidence?.signedOffAt &&
                formatDistanceToNow(new Date(assignment.packEvidence.signedOffAt), {
                  addSuffix: true,
                })}
              {assignment.adminApprovedAt &&
                !assignment.packEvidence?.signedOffAt &&
                formatDistanceToNow(new Date(assignment.adminApprovedAt), {
                  addSuffix: true,
                })}
            </span>
          </div>
        );
      }

      if (status === 'operator_complete' && validateOperatorSignature(assignment)) {
        return (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-green-600 border-green-300">
              ✔️ Signed by Operator
            </Badge>
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              ⏳ Awaiting Admin Approval
            </Badge>
          </div>
        );
      }

      if (status === 'operator_complete') {
        return (
          <Badge variant="outline" className="text-red-600 border-red-300">
            ⚠️ Incomplete Submission
          </Badge>
        );
      }

      if (status === 'admin_pending_review' || isPending) {
        return (
          <Badge variant="outline" className="text-blue-600 border-blue-300">
            🕓 Pending Sign-Off
          </Badge>
        );
      }

      if (status === 'ready_for_operator') {
        return (
          <Badge variant="outline" className="text-orange-600 border-orange-300">
            ⚠️ Ready for Operator
          </Badge>
        );
      }

      return (
        <Badge variant="outline" className="text-gray-500 border-gray-200">
          {status}
        </Badge>
      );
    },
  });

  // Actions column
  columns.push({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const assignment = row.original;
      const status = assignment.status;
      const isPending = status === 'pending';
      const operatorId = assignment.operatorId || assignment.userId;

      // Tasks not submitted by operator (pending_signoff)
      if (status === 'admin_pending_review' || isPending) {
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAdminSignOff(assignment)}
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
          >
            <ShieldCheck className="h-4 w-4 mr-1" />
            ✅ Sign Off as Admin
          </Button>
        );
      }

      // Admin Override for tasks pushed to mobile but not completed
      if (status === 'ready_for_operator') {
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAdminSignOff(assignment)}
            className="text-orange-600 border-orange-200 hover:bg-orange-50"
            title="Admin override for task not completed by operator"
          >
            <ShieldCheck className="h-4 w-4 mr-1" />
            🚨 Admin Override
          </Button>
        );
      }

      // Tasks submitted by operator
      if (status === 'operator_complete') {
        return (
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                onViewDetails(assignment.storeId, operatorId!, assignment.id)
              }
            >
              <Eye className="h-4 w-4" />
              Details
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAdminSignOff(assignment)}
              className="text-green-600 border-green-200 hover:bg-green-50"
            >
              <FileSignature className="h-4 w-4 mr-1" />
              ✅ Approve Submission
            </Button>
          </div>
        );
      }

      // Tasks already signed off by admin or other statuses
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            onViewDetails(assignment.storeId, operatorId!, assignment.id)
          }
        >
          <Eye className="h-4 w-4" />
          Details
        </Button>
      );
    },
    enableSorting: false,
  });

  return columns;
}
